import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatDateTime } from "@/lib/format";

const ENTITY_TYPES = ["Asset", "Lead", "Deal", "DealBrokerCommission", "TaxonomyValue"];

// Default the "From" filter to 7 days ago so the screen opens on recent activity.
function sevenDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

export default function AuditLog() {
  const [entityType, setEntityType] = useState("all");
  const [userId, setUserId] = useState("all");
  const [dateFrom, setDateFrom] = useState(sevenDaysAgo());
  const [dateTo, setDateTo] = useState("");

  const { data: users = [] } = useQuery({
    queryKey: ["audit-users"],
    queryFn: async () => (await api.get("/auth/users", { params: { include_inactive: true } })).data,
  });

  const params = {};
  if (entityType !== "all") params.entity_type = entityType;
  if (userId !== "all") params.user_id = userId;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["audit-log", params],
    queryFn: async () => (await api.get("/audit-log", { params })).data,
    keepPreviousData: true,
  });

  return (
    <div className="p-8" data-testid="audit-log-page">
      <div className="label-eyebrow">Admin</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
        <History className="h-6 w-6 text-slate-500" /> Audit Log
      </h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        System-wide change history for financial &amp; commercial fields — who changed what, and when.
      </p>

      {/* Filters */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2">
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="w-[200px]" data-testid="audit-filter-entity"><SelectValue placeholder="Entity type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entity types</SelectItem>
            {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger className="w-[200px]" data-testid="audit-filter-user"><SelectValue placeholder="Changed by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">From</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 font-mono-num" data-testid="audit-filter-from" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">To</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 font-mono-num" data-testid="audit-filter-to" />
        </div>
        <div className="text-xs text-slate-500 font-mono-num ml-auto">{rows.length} entries</div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table w-full text-left">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="label-eyebrow !text-slate-600">When</th>
              <th className="label-eyebrow !text-slate-600">Entity</th>
              <th className="label-eyebrow !text-slate-600">Field</th>
              <th className="label-eyebrow !text-slate-600">Old → New</th>
              <th className="label-eyebrow !text-slate-600">By</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-sm text-slate-500" data-testid="audit-empty">No audit entries match your filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`audit-entry-${r.id}`}>
                <td className="text-xs text-slate-500 font-mono-num whitespace-nowrap">{formatDateTime(r.changed_at)}</td>
                <td className="text-sm text-slate-700">{r.entity_type}</td>
                <td className="text-sm font-medium text-slate-900">{r.field_label || r.field_name}</td>
                <td className="text-sm text-slate-600">
                  <span className="text-slate-500">{r.old_value ?? "—"}</span>
                  <span className="mx-1.5 text-slate-400">→</span>
                  <span className="font-semibold text-slate-900">{r.new_value ?? "—"}</span>
                </td>
                <td className="text-sm text-slate-600">{r.changed_by_name || "system"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
